import { Box } from "@primer/react";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../state/reducers";
import { useForm } from "react-hook-form";
import { apiClient } from "../../../api/apiClient";
import { crmHocSinhApi } from "../../../api/crmHocSinhApi";
import { danTocApi } from "../../../api/danTocApi";
import { quanHeGiaDinhApi } from "../../../api/quanHeGiaDinhApi";
import { tinhHuyenXaApi } from "../../../api/tinhHuyenXaApi";
import { tonGiaoApi } from "../../../api/tonGiaoApi";
import Button from "../../../components/ui/button";
import ModalActions from "../../../components/ui/modal/ModalActions";
import { useCommonContext } from "../../../contexts/common";
import { NotifyHelper } from "../../../helpers/toast";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import { HocSinhFormSubmitRequest } from "../../../models/request/hoc-sinh/HocSinhFormSubmitRequest";
import { IHocSinh } from "../../../models/response/crm-hocsinh/IHocsinh";
import ThongTinDangKy from "./ThongTinDangKy";
import ThongTinDiaChi from "./ThongTinDiaChi";
import ThongTinHocSinh from "./ThongTinHocSinh";
import ThongTinKhac from "./ThongTinKhac";
import ThongTinLienHe from "./ThongTinLienHe";

interface PhuHuynh {
  ho_ten?: string;
  dien_thoai?: string;
  email?: string;
  dm_quanhe_id?: number;
}

interface ProfileFormProps {
  hocSinh: IHocSinh;
  phuHuynh?: PhuHuynh;
  onSubmitSuccess: (id: number) => void;
  height?: number;
}

interface FormRef {
  handleSaveChanges: () => void;
}


const ProfileForm = forwardRef<FormRef, ProfileFormProps>(
  ({ hocSinh, onSubmitSuccess, phuHuynh, height }, ref) => {
    const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
    const { translate } = useCommonContext();
    const dm_namhocs = useSelector((state: RootState) => state.categorySource.dm_namhocs);
    const { dm_truong_selected_id } = useSelector((state: RootState) => state.common);
    const [selectedProvince, setSelectedProvince] = useState<any>(
      hocSinh.dm_tinh_id || null
    );
    const [selectedDistrict, setSelectedDistrict] = useState<any>(
      hocSinh.dm_huyen_id || null
    );
    const [selectedCommune, setSelectedCommune] = useState<any>(
      hocSinh.dm_xa_id || null
    );

    const [isSaving, setIsSaving] = useState(false);
    const initialTruongId = hocSinh.dm_truong_id || dm_truong_selected_id || 0;
    const [formData, setFormData] = useState<IHocSinh>({ ...hocSinh, dm_truong_id: initialTruongId });
    const [generatedCode, setGeneratedCode] = useState<string>("");
    const [isReloadingMaTn, setIsReloadingMaTn] = useState(false);
    const [isReloadingMaHs, setIsReloadingMaHs] = useState(false);
    const [primaryContact, setPrimaryContact] = useState<{
      ho_ten?: string;
      dien_thoai?: string;
      email?: string;
      dm_quanhe_id?: number;
      quan_he?: string;
    }>({});
    const {
      register,
      handleSubmit,
      control,
      trigger,
      setValue,
      getValues,
      watch,
      formState: { errors },
    } = useForm<IHocSinh>({ mode: "all", defaultValues: formData });

    const watchedTruongId = watch("dm_truong_id");

    const generatePotentialCode = useCallback(async () => {
      setIsReloadingMaTn(true);
      try {
        const response = await crmHocSinhApi.generateStudentCode(dm_coso_id);
        if (response.is_success && response.data) {
          setGeneratedCode(response.data);
          setValue("ma_tn", response.data);
          setFormData((prev) => ({
            ...prev,
            ma_tn: response.data,
          }));
        }
      } catch (error) {
        console.error("Error generating potential code:", error);
      } finally {
        setIsReloadingMaTn(false);
      }
    }, [dm_coso_id, setValue, setFormData, setGeneratedCode]);

    const generateStudentOfficialCode = useCallback(async () => {
      setIsReloadingMaHs(true);
      try {
        const formTruongId = getValues("dm_truong_id");
        const dmTruongId = Number(
          watchedTruongId ||
          formTruongId ||
          formData.dm_truong_id ||
          hocSinh.dm_truong_id ||
          dm_truong_selected_id ||
          0
        );
        const response = await crmHocSinhApi.generateMaHs(dm_coso_id, dmTruongId);
        if (response.is_success && response.data) {
          setValue("ma_hs", response.data);
          setFormData((prev) => ({
            ...prev,
            ma_hs: response.data,
          }));
          await trigger("ma_hs");
        }
      } catch (error) {
        console.error("Error generating student code:", error);
      } finally {
        setIsReloadingMaHs(false);
      }
    }, [dm_coso_id, watchedTruongId, getValues, formData.dm_truong_id, hocSinh.dm_truong_id, dm_truong_selected_id, setValue, setFormData, trigger]);

    useEffect(() => {
      if (hocSinh) {
        const truongId = hocSinh.dm_truong_id || dm_truong_selected_id || 0;
        let _data = {
          ...hocSinh,
          nam_hoc: nam_hoc,
          dm_coso_id: dm_coso_id,
          dm_truong_id: truongId,
          ngay_sinh: hocSinh.ngay_sinh ? new Date(hocSinh.ngay_sinh) : null,
          ngay_nhap_hoc: hocSinh.ngay_nhap_hoc ? new Date(hocSinh.ngay_nhap_hoc) : null,
          ngaythi_dauvao_dukien: hocSinh.ngaythi_dauvao_dukien ? new Date(hocSinh.ngaythi_dauvao_dukien) : null,
        };
        setFormData(_data);

        setValue("nam_hoc", nam_hoc);
        if (truongId > 0) {
          setValue("dm_truong_id", truongId);
        }

        if (hocSinh.id > 0) {
          // Load thông tin lớp theo năm học hiện tại (tránh trường hợp dm_lop_id của học sinh
          // thuộc năm học cũ, không khớp với danh sách lớp của năm học đang chọn)
          crmHocSinhApi.getHSCCById(hocSinh.id, nam_hoc).then((res) => {
            if (res.is_success && res.data) {
              const lopData = {
                dm_truong_id: res.data.dm_truong_id || hocSinh.dm_truong_id || truongId,
                dm_khoi_id: res.data.dm_khoi_id || hocSinh.dm_khoi_id || 0,
                dm_he_id: res.data.dm_he_id || hocSinh.dm_he_id || 0,
                dm_lop_id: res.data.dm_lop_id || 0,
                dm_trangthaihocsinh_id: res.data.dm_trangthaihocsinh_id || hocSinh.dm_trangthaihocsinh_id || 0,
                ngay_nhap_hoc_lai: res.data.ngay_nhap_hoc_lai ? new Date(res.data.ngay_nhap_hoc_lai) : null,
              };
              setFormData((prev) => ({ ...prev, ...lopData }));
              Object.entries(lopData).forEach(([key, value]) => {
                setValue(key as keyof IHocSinh, value);
              });
            }
          });
        } else {
          generatePotentialCode();
        }
      }
    }, [hocSinh, nam_hoc, dm_coso_id, dm_truong_selected_id, setValue, generatePotentialCode]);

    useEffect(() => {
      if (hocSinh.id === 0) {
        const setDefaults = async () => {
          try {
            const [quocTichRes, danTocRes, tonGiaoRes] = await Promise.all([
              tinhHuyenXaApi.selectQuocTich(),
              danTocApi.selectAll(),
              tonGiaoApi.selectAll(),
            ]);
            if (quocTichRes.is_success && quocTichRes.data) {
              const vietNam = quocTichRes.data.find((x: any) =>
                x.quoc_tich?.toLowerCase().includes("việt nam") ||
                x.quoc_tich?.toLowerCase().includes("viet nam")
              );
              if (vietNam) {
                setValue("dm_quoctich_id", vietNam.id);
                setFormData((prev) => ({ ...prev, dm_quoctich_id: vietNam.id }));
              }
            }
            if (danTocRes.is_success && danTocRes.data) {
              const kinh = danTocRes.data.find((x: any) =>
                x.dan_toc?.toLowerCase() === "kinh"
              );
              if (kinh) {
                setValue("dm_dantoc_id", kinh.id);
                setFormData((prev) => ({ ...prev, dm_dantoc_id: kinh.id }));
              }
            }
            if (tonGiaoRes.is_success && tonGiaoRes.data) {
              const khong = tonGiaoRes.data.find((x: any) =>
                x.ton_giao?.toLowerCase() === "không" ||
                x.ton_giao?.toLowerCase() === "khong"
              );
              if (khong) {
                setValue("dm_tongiao_id", khong.id);
                setFormData((prev) => ({ ...prev, dm_tongiao_id: khong.id }));
              }
            }
          } catch (error) {
            console.error("Error setting default values:", error);
          }
        };
        setDefaults();

        if (!formData.nam_nhap_hoc) {
          const defaultNamNhapHoc = dm_namhocs.find((x: any) =>
            x.nam_hoc?.toString().includes("2026 - 2027") ||
            x.nam_hoc?.toString().includes("2026-2027")
          );
          if (defaultNamNhapHoc) {
            setValue("nam_nhap_hoc", defaultNamNhapHoc.nam_hoc);
            setFormData((prev) => ({ ...prev, nam_nhap_hoc: defaultNamNhapHoc.nam_hoc }));
          }
        }
      }
    }, [hocSinh.id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      const loadPrimaryContact = async () => {
        if (hocSinh.id > 0) {
          try {
            const res = await quanHeGiaDinhApi.selectByHocSinh(hocSinh.id);
            if (res.is_success && res.data) {
              const primaryRelation = res.data.find((relation: any) => relation.is_uutien);
              if (primaryRelation && primaryRelation.ts_phuhuynh) {
                setPrimaryContact({
                  ho_ten: primaryRelation.ts_phuhuynh.ho_ten,
                  dien_thoai: primaryRelation.ts_phuhuynh.dien_thoai,
                  email: primaryRelation.ts_phuhuynh.email,
                  dm_quanhe_id: primaryRelation.dm_quanhe_id,
                  quan_he: primaryRelation.ts_phuhuynh.quan_he,
                });
              } else {
                setPrimaryContact({});
              }
            }
          } catch (error) {
            console.error("Error loading primary contact:", error);
            setPrimaryContact({});
          }
        } else {
          setPrimaryContact({});
        }
      };

      loadPrimaryContact();
    }, [hocSinh.id]);

    const handleProvinceChange = (value: any) => {
      setSelectedProvince(value);
      setSelectedDistrict(null);
      setSelectedCommune(null);
      setFormData((prev) => ({
        ...prev,
        dm_tinh_id: value,
        dm_huyen_id: 0,
        dm_xa_id: 0,
      }));
    };
    const handleDistrictChange = (value: any) => {
      setSelectedDistrict(value);
      setSelectedCommune(null);
      setFormData((prev) => ({
        ...prev,
        dm_huyen_id: value,
        dm_xa_id: 0,
      }));
    };
    const handleCommuneChange = (value: any) => {
      setSelectedCommune(value);
      setFormData((prev) => ({
        ...prev,
        dm_xa_id: value,
      }));
    };

    const handleKhoiChange = (value: number) => {
      setFormData((prev) => ({
        ...prev,
        dm_khoi_id: value,
        dm_he_id: 0,
        dm_lop_id: 0,
      }));
      setValue("dm_he_id", 0);
      setValue("dm_lop_id", 0);
    };


    const handleSaveChanges = async (data: any) => {
      setIsSaving(true);

      const finalMaTn = data.id === 0 ? (generatedCode || data.ma_tn || "") : (data.ma_tn || "");

      // Validate bắt buộc: dm_truong_id, dm_khoi_id, dm_he_id, dm_lop_id phải > 0
      const dmTruongId = data.dm_truong_id || formData.dm_truong_id || 0;
      const dmKhoiId = data.dm_khoi_id || formData.dm_khoi_id || 0;
      const dmHeId = data.dm_he_id || formData.dm_he_id || 0;
      const dmLopId = data.dm_lop_id || formData.dm_lop_id || 0;

      if (!dmTruongId || dmTruongId <= 0) {
        NotifyHelper.Error("Vui lòng chọn cấp học");
        setIsSaving(false);
        return;
      }
      if (!dmKhoiId || dmKhoiId <= 0) {
        NotifyHelper.Error("Vui lòng chọn khối");
        setIsSaving(false);
        return;
      }
      if (!dmHeId || dmHeId <= 0) {
        NotifyHelper.Error("Vui lòng chọn hệ");
        setIsSaving(false);
        return;
      }
      if (!dmLopId || dmLopId <= 0) {
        NotifyHelper.Error("Vui lòng chọn lớp");
        setIsSaving(false);
        return;
      }

      const formatDateForBackend = (date: Date | null | undefined): string | null => {
        if (!date) return null;
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const formatDateToTimestamp = (date: Date | null | undefined): number => {
        if (!date) return 0;
        const d = new Date(date);
        const year = d.getFullYear();
        const month = d.getMonth();
        const day = d.getDate();
        return new Date(Date.UTC(year, month, day)).getTime();
      };

      const formDataSubmit: HocSinhFormSubmitRequest = {
        id: data.id,
        ma_hs: data.ma_hs ?? "",
        ma_tn: finalMaTn,
        nam_hoc: data.nam_hoc ?? nam_hoc,
        ho_ten: data.ho_ten ?? "",
        ho_ten_en: data.ho_ten_en ?? "",
        ngay_sinh_timestamp: formatDateToTimestamp(data.ngay_sinh),
        dm_gioitinh_id: data.dm_gioitinh_id,
        dia_chi_noi_sinh: data.dia_chi_noi_sinh ?? "",
        truong_hoc_cu: data.truong_hoc_cu ?? "",
        ho_ten_lien_he: data.ho_ten_lien_he ?? "",
        dien_thoai_lien_he: data.dien_thoai_lien_he ?? "",
        email_lien_he: data.email_lien_he ?? "",
        dm_chiendich_id: data.dm_chiendich_id,
        dm_quanhe_id: data.dm_quanhe_id,
        dm_nguonbiettoi_id: data.dm_nguonbiettoi_id,
        dm_tinh_id: data.dm_tinh_id,
        dm_huyen_id: data.dm_huyen_id,
        dm_xa_id: data.dm_xa_id,
        dia_chi_tt: data.dia_chi_tt ?? "",
        dm_truong_id: dmTruongId,
        dm_khoi_id: dmKhoiId,
        dm_lop_id: dmLopId,
        dm_he_id: dmHeId,
        dm_quoctich_id: data.dm_quoctich_id,
        dm_coso_id: data.dm_coso_id ?? dm_coso_id,
        ngaythi_dauvao_dukien: formatDateForBackend(data.ngaythi_dauvao_dukien) as any,
        ngay_nhap_hoc: formatDateForBackend(data.ngay_nhap_hoc) as any,
        ngay_nhap_hoc_lai: formatDateForBackend(formData.ngay_nhap_hoc_lai),
        que_quan: data.que_quan ?? "",
        nam_nhap_hoc: data.nam_nhap_hoc ?? "",
        email_hocsinh: data.email_hocsinh ?? "",
        dien_thoai_hocsinh: data.dien_thoai_hocsinh ?? "",
        ma_so_bhxh: data.ma_so_bhxh ?? "",
        loai_dang_ky_bhxh: data.loai_dang_ky_bhxh ?? "",
        so_tien_bhxh: data.so_tien_bhxh ?? 0,
        noi_dang_ky_kcb_bandau: data.noi_dang_ky_kcb_bandau ?? "",
        doi_tuong_chinh_sach: data.doi_tuong_chinh_sach ?? "",
        ma_cua_bo: data.ma_cua_bo ?? "",
        ma_cu: data.ma_cu ?? "",
        chung_minh_thu_nguoilienhe: "",
        noi_cap_chung_minh_thu_nguoilienhe: "",
        ngay_cap_chung_minh_thu_nguoilienhe: undefined,
        ngay_sinh_nguoilienhe: undefined,
        quoc_tich_id_nguoilienhe: data.dm_quoctich_id,
        nghe_nghiep_nguoilienhe: "",
        noi_lam_viec_nguoilienhe: "",
        dia_chi_thuong_tru_nguoilienhe: "",
        dm_dantoc_id: data.dm_dantoc_id,
        dm_tongiao_id: data.dm_tongiao_id,
        ma_dinh_danh: data.ma_dinh_danh ?? "",
        tt_suc_khoe: data.tt_suc_khoe ?? "",
        dia_chi_hientai: data.dia_chi_hientai ?? "",
        chieu_cao: data.chieu_cao ?? "",
        can_nang: data.can_nang ?? "",
        so_dien_thoai_bo: "",
        so_dien_thoai_me: "",
        ho_chieu: data.ho_chieu ?? "",
        ngay_ketthuc_hoc: data.ngay_ketthuc_hoc ?? undefined,
        ma_dinh_danh_csdl: data.ma_dinh_danh_csdl ?? "",
        ma_so_bhyt: data.ma_so_bhyt ?? "",
        ghi_chu_chung: data.ghi_chu_chung ?? '',

        dm_trangthaihocsinh_id: data.dm_trangthaihocsinh_id ?? 0,
        is_nhucau_noitru: data.is_nhucau_noitru ?? false,
        uy_quyen_cho_truong: data.uy_quyen_cho_truong ?? false,
        is_nhucau_dangky_bus: (data as any).is_nhucau_dangky_bus ?? false,
        is_dangky_ansang: (data as any).is_dangky_ansang ?? false,
        laconthu: data.laconthu ?? 1

      };
      console.log("formDataSubmit:", formDataSubmit);

      const isValid = await trigger();
      if (!isValid) {
        setIsSaving(false);
        return;
      }

      if (formData.id > 0 && finalMaTn) {
        try {
          const checkMaTn = await crmHocSinhApi.selectByMaTn(finalMaTn, formDataSubmit.dm_coso_id);
          if (checkMaTn.is_success && checkMaTn.data && checkMaTn.data.id !== formData.id) {
            NotifyHelper.Error(`Mã tiềm năng '${finalMaTn}' đã tồn tại trong hệ thống`);
            setIsSaving(false);
            return;
          }
        } catch { /* ignore */ }
      }

      if (formDataSubmit.ma_hs && !/^(TD|MN)\d{6}$/.test(formDataSubmit.ma_hs)) {
        NotifyHelper.Error(`Mã học sinh '${formDataSubmit.ma_hs}' không đúng định dạng. Phải bắt đầu bằng 'TD' hoặc 'MN' và có đúng 6 chữ số phía sau (VD: TD123456 hoặc MN123456)`);
        setIsSaving(false);
        return;
      }

      if (formDataSubmit.ma_hs) {
        try {
          const checkMaHs = await crmHocSinhApi.selectByMaHs(formDataSubmit.ma_hs, formDataSubmit.dm_coso_id);
          if (checkMaHs.is_success && checkMaHs.data && checkMaHs.data.id !== formData.id) {
            NotifyHelper.Error(`Mã học sinh '${formDataSubmit.ma_hs}' đã tồn tại trong hệ thống`);
            setIsSaving(false);
            return;
          }
        } catch { /* ignore */ }
      }

      let res;
      if (formData.id > 0) {
        res = await crmHocSinhApi.update(formDataSubmit);
      } else {
        res = await crmHocSinhApi.insert(formDataSubmit);
      }

      if (res.is_success) {
        NotifyHelper.Success(
          formData.id > 0 ? "Cập nhật thành công" : "Thêm mới thành công"
        );

        const savedHocSinhId = formData.id > 0 ? formData.id : res.data;
        const originalNgayNhapHoc = hocSinh.ngay_nhap_hoc
          ? new Date(hocSinh.ngay_nhap_hoc).toDateString()
          : null;
        const newNgayNhapHoc = data.ngay_nhap_hoc
          ? new Date(data.ngay_nhap_hoc).toDateString()
          : null;
        const ngayNhapHocChanged =
          formData.id === 0
            ? !!newNgayNhapHoc
            : newNgayNhapHoc !== originalNgayNhapHoc;

        if (ngayNhapHocChanged && savedHocSinhId) {
          try {
            await apiClient.post(
              `api/uf_saleset/sync_cong_no_hocsinh`,
              {
                nam_hoc: formDataSubmit.nam_hoc,
                ts_hocsinh_ids: [savedHocSinhId],
                dm_truong_ids: [],
                dm_khoi_ids: [],
                dm_he_ids: [],
              },
              "https://finance.truongxanh.edu.vn"
            );
          } catch (syncError) {
            console.error("Error syncing to Finance:", syncError);
          }
        }

        onSubmitSuccess(res.data);
      } else {
        NotifyHelper.Error(res.message ?? "");
      }
      setIsSaving(false);
    };

    return (


      <form onSubmit={handleSubmit(handleSaveChanges)}>
        <Box sx={{
          display: "grid",
          gap: "24px",
          position: "relative",
        }}>
          <ThongTinHocSinh
            control={control}
            hocSinh={hocSinh}
            setFormData={setFormData}
            setValue={setValue}
            generatedCode={generatedCode}
            errors={errors}
            register={register}
            formData={formData}
            trigger={trigger}
            onReloadMaTn={generatePotentialCode}
            onReloadMaHs={generateStudentOfficialCode}
            isReloadingMaTn={isReloadingMaTn}
            isReloadingMaHs={isReloadingMaHs}
          />
          <ThongTinDangKy
            control={control}
            hocSinh={hocSinh}
            setFormData={setFormData}
            setValue={setValue}
            generatedCode={generatedCode}
            errors={errors}
            register={register}
            formData={formData}
            trigger={trigger}

          />
          <ThongTinDiaChi
            control={control}
            hocSinh={hocSinh}
            setFormData={setFormData}
            setValue={setValue}
            generatedCode={generatedCode}
            errors={errors}
            register={register}
            formData={formData}
            trigger={trigger}
          />
          <ThongTinKhac
            control={control}
            hocSinh={hocSinh}
            setFormData={setFormData}
            setValue={setValue}
            generatedCode={generatedCode}
            errors={errors}
            register={register}
            formData={formData}
            trigger={trigger}
          />
          <ThongTinLienHe
            control={control}
            hocSinh={hocSinh}
            setFormData={setFormData}
            setValue={setValue}
            generatedCode={generatedCode}
            errors={errors}
            register={register}
            formData={formData}
            trigger={trigger}
            primaryContact={primaryContact}
          />
          <Box sx={{
            backdropFilter: "blur(1px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
            position: "sticky",
            bottom: 0,
          }}>

            <Button
              text="Base.Label.Save"
              variant="primary"
              type="submit"
              size="large"
              block
              isLoading={isSaving}
              sx={{
                width: "250px",
                boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px;",
                borderRadius: "32px",
              }}
            />

          </Box>
        </Box>

      </form>


    );
  }
);

export default ProfileForm;
